import { useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import NeuralCanvas from './NeuralCanvas';
import ClickWowEffect from './ClickWowEffect';

const VIDEO_URL = "https://d8j0ntlcm91z4.cloudfront.net/user_3A50ZOlk9uxVbCULyyuvTjaRsvL/hf_20260227_093354_851ab2de-30cb-4b0c-994e-089acce680b8.mp4";

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.18 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.25, 0.1, 0.25, 1] } },
};

export default function HeroSection() {
  const videoRef = useRef(null);

  const scrollToProtocol = useCallback(() => {
    const el = document.getElementById('hero-text');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <>
      {/* Full-bleed Video Section */}
      <section
        id="hero"
        data-testid="hero-section"
        className="relative w-full overflow-hidden"
        style={{ height: '100vh' }}
      >
        <video
          ref={videoRef}
          src={VIDEO_URL}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: 'center 38%' }}
        />
        {/* Edge vignette */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.55) 100%)',
          }}
        />
        {/* Bottom fade to black */}
        <div
          className="absolute bottom-0 left-0 right-0 pointer-events-none"
          style={{ height: '30%', background: 'linear-gradient(to top, #000 0%, transparent 100%)' }}
        />
        {/* Click wow effect canvas */}
        <ClickWowEffect onScrollToProtocol={scrollToProtocol} />

        {/* CLICK TO CONTINUE — perfectly centered across all screen sizes */}
        <motion.div
          className="pointer-events-none flex flex-col items-center gap-4"
          style={{
            position: 'absolute',
            bottom: '72px',
            left: 0,
            right: 0,
            zIndex: 20,
            textAlign: 'center',
          }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 1 }}
        >
          {/* Outer pulsing ring */}
          <div className="relative flex items-center justify-center">
            <motion.div
              className="absolute rounded-full border"
              style={{ width: 90, height: 90, borderColor: 'rgba(0,240,255,0.18)' }}
              animate={{ scale: [1, 1.35, 1], opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut' }}
            />
            <motion.div
              className="absolute rounded-full border"
              style={{ width: 90, height: 90, borderColor: 'rgba(0,240,255,0.25)' }}
              animate={{ scale: [1, 1.7, 1], opacity: [0.4, 0, 0.4] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut', delay: 0.5 }}
            />
            {/* Inner glowing button */}
            <motion.div
              className="w-16 h-16 rounded-full flex items-center justify-center relative"
              style={{
                background: 'rgba(0, 240, 255, 0.08)',
                border: '1.5px solid rgba(0, 240, 255, 0.6)',
                boxShadow: '0 0 20px rgba(0, 240, 255, 0.25), inset 0 0 20px rgba(0, 240, 255, 0.05)',
              }}
              animate={{ boxShadow: [
                '0 0 20px rgba(0,240,255,0.2), inset 0 0 20px rgba(0,240,255,0.05)',
                '0 0 40px rgba(0,240,255,0.5), inset 0 0 30px rgba(0,240,255,0.12)',
                '0 0 20px rgba(0,240,255,0.2), inset 0 0 20px rgba(0,240,255,0.05)',
              ]}}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            >
              {/* Down chevron */}
              <motion.svg
                width="20" height="20" viewBox="0 0 20 20" fill="none"
                animate={{ y: [0, 4, 0] }}
                transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
              >
                <path d="M4 7L10 13L16 7" stroke="#00F0FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </motion.svg>
            </motion.div>
          </div>

          {/* Label */}
          <motion.span
            className="font-mono text-[#00F0FF] uppercase tracking-[0.35em] text-xs font-medium"
            style={{ textShadow: '0 0 12px rgba(0,240,255,0.6)', letterSpacing: '0.35em' }}
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            Click to continue
          </motion.span>
        </motion.div>

        {/* Remove old scroll cue — replaced above */}
      </section>

      {/* Hero Text — immediately below video */}
      <section
        id="hero-text"
        className="relative bg-black overflow-hidden"
        style={{ paddingTop: '100px', paddingBottom: '120px' }}
        data-testid="hero-text-section"
      >
        <NeuralCanvas opacity={0.15} />
        {/* Grid bg */}
        <div className="grid-bg absolute inset-0" />
        {/* Ambient glow orbs */}
        <div className="glow-orb" style={{ width: 500, height: 500, background: 'rgba(0, 240, 255, 0.04)', top: '10%', left: '-10%' }} />
        <div className="glow-orb float-delayed" style={{ width: 400, height: 400, background: 'rgba(74, 144, 217, 0.05)', bottom: '5%', right: '-5%' }} />

        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
          >
            <motion.p
              variants={itemVariants}
              className="font-mono text-[#00F0FF] text-xs tracking-[0.35em] uppercase mb-6"
            >
              NeuroMesh Protocol — v1.0
            </motion.p>

            <motion.h1
              variants={itemVariants}
              className="font-display text-white leading-[0.92] tracking-[-0.03em] mb-8 glitch"
              data-text="The Intelligence Layer"
              style={{ fontSize: 'clamp(48px, 8vw, 100px)', fontFamily: "'Syne', sans-serif" }}
            >
              The Intelligence Layer<br />
              <span className="gradient-text shimmer-text">for On-Robot Compute</span>
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="text-[#888] leading-relaxed max-w-2xl mb-12"
              style={{ fontSize: 'clamp(18px, 2vw, 24px)' }}
            >
              Privacy-first robotics. Decentralized intelligence. Real-world value.
            </motion.p>

            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4"
            >
              <a
                href="https://app.neuro-mesh.io"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-block text-center"
                data-testid="hero-open-dapp-btn"
              >
                Open dApp
              </a>
              <a
                href="https://whitepaper.neuro-mesh.io"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary inline-block text-center"
                data-testid="hero-whitepaper-btn"
              >
                Read Whitepaper
              </a>
            </motion.div>

            {/* Trust signals */}
            <motion.div
              variants={itemVariants}
              className="flex flex-wrap items-center gap-8 mt-16 pt-12 border-t border-white/5"
            >
              {[
                { label: 'Built on Solana', val: 'Ultra-fast' },
                { label: 'Privacy Model', val: 'Edge-first' },
                { label: 'Verified Outputs', val: 'PoI + zk' },
                { label: 'Deployment', val: 'Humanoids' },
              ].map((s) => (
                <div key={s.label}>
                  <div className="font-display text-white font-semibold text-base">{s.val}</div>
                  <div className="font-mono text-[#555] text-xs tracking-wider uppercase mt-0.5">{s.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
